<template>
  <!-- 工具栏 -->
  <div class="toolbar">
    <div class="search-box">
      <svg viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd"/>
      </svg>
      <input v-model="search" placeholder="搜索订单号 / 商品名..." @keydown="onSearchKey" />
    </div>
    <div class="filter-group">
      <select v-model="statusFilter">
        <option value="">全部状态</option>
        <option value="paid">已支付</option>
        <option value="unpaid">未支付</option>
        <option value="closed">已关闭</option>
        <option value="refunded">已退款</option>
      </select>
      <select v-model="execFilter">
        <option value="">全部执行</option>
        <option value="pending">待执行</option>
        <option value="running">运行中</option>
        <option value="success">成功</option>
        <option value="failed">失败</option>
      </select>
      <!-- 批量删除按钮 -->
      <button
        v-if="selected.length > 0"
        class="btn btn-danger-batch"
        @click="handleBatchDelete"
        :disabled="batchDeleting"
      >
        <svg viewBox="0 0 20 20" fill="currentColor" style="width:14px;height:14px">
          <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/>
        </svg>
        {{ batchDeleting ? '删除中...' : `删除选中 (${selected.length})` }}
      </button>
      <button class="btn btn-primary" @click="fetchOrders" :disabled="loading">
        <svg v-if="!loading" viewBox="0 0 20 20" fill="currentColor" style="width:14px;height:14px">
          <path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clip-rule="evenodd"/>
        </svg>
        <svg v-else class="spin" viewBox="0 0 20 20" fill="currentColor" style="width:14px;height:14px">
          <path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1z" clip-rule="evenodd"/>
        </svg>
        {{ loading ? '刷新中...' : '刷新' }}
      </button>
    </div>
  </div>

  <!-- 加载状态 -->
  <div v-if="loading" class="state-box loading">
    <div class="spinner"></div>
    <span>加载订单数据中...</span>
  </div>

  <!-- 空状态 -->
  <div v-else-if="!orders || orders.length === 0" class="state-box empty">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
      <line x1="9" y1="12" x2="15" y2="12"/>
      <line x1="9" y1="16" x2="15" y2="16"/>
    </svg>
    <p>暂无订单数据</p>
    <span>当前筛选条件下没有找到订单</span>
  </div>

  <!-- 数据表格 -->
  <div v-else class="table-card">
    <div class="table-scroll">
      <table class="data-table">
        <colgroup>
          <col class="col-order" />
          <col class="col-name" />
          <col class="col-amount" />
          <col class="col-machine" />
          <col class="col-status" />
          <col class="col-exec" />
          <col class="col-time" />
          <col class="col-action" />
          <col class="col-check" />
        </colgroup>
        <thead>
          <tr>
            <th>订单号</th>
            <th>商品名称</th>
            <th>金额</th>
            <th>客户机器码</th>
            <th>支付状态</th>
            <th>执行状态</th>
            <th>创建时间</th>
            <th>操作</th>
            <th class="th-check">
              <input type="checkbox" :checked="allSelected" @change="toggleSelectAll" />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="o in orders" :key="o.out_trade_no" :class="{ selected: selected.includes(o.out_trade_no) }">
            <td>
              <code class="order-id">{{ o.out_trade_no }}</code>
            </td>
            <td class="name-cell">{{ o.subject || '-' }}</td>
            <td class="amount">&yen;{{ Number(o.total_amount).toFixed(2) }}</td>
            <td class="mono">
              {{ (o.client_machine_id || '-').slice(0, 12) }}
              {{ o.client_machine_id && o.client_machine_id.length > 12 ? '...' : '' }}
            </td>
            <td>
              <span :class="['badge', statusBadge(o.status)]">
                {{ statusLabel(o.status) }}
              </span>
            </td>
            <td>
              <span :class="['badge exec-badge', execBadge(o.exec_status)]">
                {{ execLabel(o.exec_status) }}
              </span>
            </td>
            <td class="time-col">{{ formatTime(o.gmt_create || o.created_at) }}</td>
            <td>
              <button class="btn-link" @click="viewDetail(o)">详情</button>
            </td>
            <td class="td-check">
              <input type="checkbox" :value="o.out_trade_no" v-model="selected" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 分页条 -->
    <div class="pagination-bar">
      <!-- 每页条数 -->
      <div class="page-size-control">
        <span>每页</span>
        <select v-model.number="pageSize">
          <option :value="10">10</option>
          <option :value="20">20</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
        <span>条</span>
      </div>

      <!-- 页码信息 -->
      <div class="page-info">
        共 <strong>{{ total }}</strong> 条，
        第 <strong>{{ page }}</strong> / <strong>{{ totalPages || 1 }}</strong> 页
      </div>

      <!-- 页码切换 -->
      <div class="page-nav">
        <button :disabled="page <= 1" @click="page = 1" title="首页">&laquo;</button>
        <button :disabled="page <= 1" @click="page--">&lsaquo;</button>
        <span class="page-nums">
          <button
            v-for="p in pageNumbers"
            :key="p"
            :class="{ active: p === page, ellipsis: p === '...' }"
            :disabled="p === '...'"
            @click="p !== '...' && (page = p)"
          >{{ p }}</button>
        </span>
        <button :disabled="page >= totalPages" @click="page++">&rsaquo;</button>
        <button :disabled="page >= totalPages" @click="page = totalPages" title="末页">&raquo;</button>
      </div>
    </div>
  </div>

  <!-- 订单详情弹窗 -->
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="showDetail" class="modal-overlay" @click.self="showDetail = false">
        <div class="modal-box">
          <div class="modal-head">
            <h3>订单详情</h3>
            <div class="modal-actions">
              <button
                v-if="detailOrder && detailOrder.status === 'paid'"
                class="btn btn-danger"
                :disabled="actionLoading"
                @click="handleRefund"
              >
                {{ actionLoading ? '退款中...' : '退款' }}
              </button>
              <button
                v-if="detailOrder"
                class="btn btn-gray"
                @click="handleDelete"
              >
                删除
              </button>
              <button class="modal-close" @click="showDetail = false">&times;</button>
            </div>
          </div>
          <div class="modal-body" v-if="detailOrder">
            <div class="detail-grid">
              <div class="dg-row">
                <span class="dg-k">商户订单号</span>
                <span class="dg-v mono">{{ detailOrder.out_trade_no }}</span>
              </div>
              <div class="dg-row">
                <span class="dg-k">支付宝交易号</span>
                <span class="dg-v">{{ detailOrder.trade_no || '-' }}</span>
              </div>
              <div class="dg-row">
                <span class="dg-k">商品名称</span>
                <span class="dg-v strong">{{ detailOrder.subject }}</span>
              </div>
              <div class="dg-row">
                <span class="dg-k">支付金额</span>
                <span class="dg-v amount-lg">&yen;{{ detailOrder.total_amount }}</span>
              </div>
              <div class="dg-row">
                <span class="dg-k">支付状态</span>
                <span class="dg-v">
                  <span :class="['badge', statusBadge(detailOrder.status)]">
                    {{ statusLabel(detailOrder.status) }}
                  </span>
                </span>
              </div>
              <div class="dg-row">
                <span class="dg-k">执行状态</span>
                <span class="dg-v">
                  <span :class="['badge exec-badge', execBadge(detailOrder.exec_status)]">
                    {{ execLabel(detailOrder.exec_status) }}
                  </span>
                </span>
              </div>
              <div class="dg-row">
                <span class="dg-k">客户机器码</span>
                <span class="dg-v mono">{{ detailOrder.client_machine_id || '-' }}</span>
              </div>
              <div class="dg-row">
                <span class="dg-k">客户端版本</span>
                <span class="dg-v">{{ detailOrder.client_version || '-' }}</span>
              </div>
              <div class="dg-row dg-full">
                <span class="dg-k">购买项目</span>
                <pre class="dg-pre">{{ formatJson(detailOrder.items) }}</pre>
              </div>
              <div class="dg-row">
                <span class="dg-k">创建时间</span>
                <span class="dg-v">{{ formatTime(detailOrder.gmt_create || detailOrder.created_at) }}</span>
              </div>
              <div class="dg-row" v-if="detailOrder.gmt_payment">
                <span class="dg-k">支付时间</span>
                <span class="dg-v">{{ formatTime(detailOrder.gmt_payment) }}</span>
              </div>
              <div class="dg-row" v-if="detailOrder.status === 'refunded'">
                <span class="dg-k">退款时间</span>
                <span class="dg-v">{{ formatTime(detailOrder.gmt_refund || detailOrder.refunded_at) }}</span>
              </div>
            </div>
          </div>
          <div class="modal-foot">
            <button class="btn btn-secondary" @click="showDetail = false">关闭</button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'

const API_BASE = import.meta.env.VITE_API_BASE || ''

// 状态
const orders = ref([])
const selected = ref([])
const loading = ref(false)
const search = ref('')
const statusFilter = ref('')
const execFilter = ref('')
const page = ref(1)
const pageSize = ref(10)
const total = ref(0)
const totalPages = ref(0)

// 详情弹窗
const showDetail = ref(false)
const detailOrder = ref(null)
const actionLoading = ref(false)
const batchDeleting = ref(false)

// 全选
const allSelected = computed(() => orders.value.length > 0 && selected.value.length === orders.value.length)

function toggleSelectAll(e) {
  if (e.target.checked) {
    selected.value = orders.value.map(o => o.out_trade_no)
  } else {
    selected.value = []
  }
}

// 批量删除
async function handleBatchDelete() {
  if (selected.value.length === 0) return
  if (!confirm(`确认删除选中的 ${selected.value.length} 个订单？此操作不可撤销。`)) return
  batchDeleting.value = true
  try {
    const res = await adminFetch(`${API_BASE}/api/admin/orders/batch`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ out_trade_nos: selected.value }),
    })
    const data = await res.json()
    if (data.success) {
      selected.value = []
      alert(`已删除 ${data.deleted ?? selected.value.length} 个订单`)
      fetchOrders()
    } else {
      alert(data.message || '删除失败')
    }
  } catch (e) {
    if (e.message !== 'Unauthorized') alert('网络错误: ' + e.message)
  } finally {
    batchDeleting.value = false
  }
}

// 页码数字列表（最多显示 7 个，带省略号）
const pageNumbers = computed(() => {
  const tp = totalPages.value
  const cur = page.value
  if (tp <= 7) return Array.from({ length: tp }, (_, i) => i + 1)
  const nums = []
  if (cur <= 4) {
    for (let i = 1; i <= 5; i++) nums.push(i)
    nums.push('...')
    nums.push(tp)
  } else if (cur >= tp - 3) {
    nums.push(1)
    nums.push('...')
    for (let i = tp - 4; i <= tp; i++) nums.push(i)
  } else {
    nums.push(1)
    nums.push('...')
    for (let i = cur - 1; i <= cur + 1; i++) nums.push(i)
    nums.push('...')
    nums.push(tp)
  }
  return nums
})

// 切换筛选条件 / 每页条数时重置到第一页；翻页时清空选中
watch([statusFilter, execFilter, pageSize], () => {
  page.value = 1
  fetchOrders()
})

watch(page, () => {
  selected.value = []
  fetchOrders()
})

// 获取订单列表
async function fetchOrders() {
  loading.value = true
  try {
    const params = new URLSearchParams({
      page: page.value,
      size: pageSize.value,
    })
    if (statusFilter.value) params.set('status', statusFilter.value)
    if (execFilter.value) params.set('exec_status', execFilter.value)
    if (search.value.trim()) params.set('search', search.value.trim())

    const res = await adminFetch(`${API_BASE}/api/admin/orders?${params}`)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    if (data.success) {
      orders.value = data.orders || []
      total.value = data.total ?? 0
      totalPages.value = data.totalPages ?? 0
      page.value = data.page ?? 1
    }
  } catch (e) {
    if (e.message !== 'Unauthorized') {
      console.error('[Orders] Fetch error:', e)
      alert('获取订单失败: ' + e.message)
    }
  } finally {
    loading.value = false
  }
}

// 回车搜索
function onSearchKey(e) {
  if (e.key === 'Enter') {
    page.value = 1
    fetchOrders()
  }
}

// 查看详情
function viewDetail(order) {
  detailOrder.value = order
  showDetail.value = true
}

// admin API 统一请求，自动处理 401 跳转登录页
async function adminFetch(url, options = {}) {
  const token = localStorage.getItem('admin_token')
  const res = await fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      'Authorization': `Bearer ${token}`,
    },
  })
  if (res.status === 401) {
    localStorage.removeItem('admin_token')
    localStorage.removeItem('admin_user')
    window.location.reload()
    throw new Error('Unauthorized')
  }
  return res
}

// 退款
async function handleRefund() {
  if (!detailOrder.value) return
  if (!confirm(`确认退款订单 ${detailOrder.value.out_trade_no}？金额 ¥${detailOrder.value.total_amount}。此操作不可撤销。`)) return
  actionLoading.value = true
  try {
    const res = await adminFetch(`${API_BASE}/api/admin/orders/${detailOrder.value.out_trade_no}/refund`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    })
    const data = await res.json()
    if (data.success) {
      showDetail.value = false
      alert('退款成功')
      fetchOrders()
    } else {
      alert(data.message || '退款失败')
    }
  } catch (e) {
    if (e.message !== 'Unauthorized') alert('网络错误: ' + e.message)
  } finally {
    actionLoading.value = false
  }
}

// 删除订单
async function handleDelete() {
  if (!detailOrder.value) return
  if (!confirm(`确认删除订单 ${detailOrder.value.out_trade_no}？此操作不可撤销。`)) return
  try {
    const res = await adminFetch(`${API_BASE}/api/admin/orders/${detailOrder.value.out_trade_no}`, {
      method: 'DELETE',
    })
    const data = await res.json()
    if (data.success) {
      showDetail.value = false
      alert('订单已删除')
      fetchOrders()
    } else {
      alert(data.message || '删除失败')
    }
  } catch (e) {
    if (e.message !== 'Unauthorized') alert('网络错误: ' + e.message)
  }
}

// 状态标签
function statusLabel(s) {
  return { unpaid: '未支付', paid: '已支付', closed: '已关闭', refunded: '已退款', expired: '已过期' }[s] || s || '-'
}

function statusBadge(s) {
  return { paid: 'ok', unpaid: 'warn', closed: 'muted', refunded: 'err', expired: 'muted' }[s] || 'muted'
}

function execLabel(s) {
  return { pending: '待执行', running: '运行中', success: '成功', failed: '失败' }[s] || s || '-'
}

function execBadge(s) {
  return { success: 'ok', pending: 'warn', running: 'running', failed: 'err' }[s] || 'muted'
}

function formatTime(t) {
  if (!t) return '-'
  try { return new Date(t).toLocaleString('zh-CN') } catch { return t }
}

function formatJson(v) {
  if (!v) return '-'
  try { return typeof v === 'string' ? JSON.stringify(JSON.parse(v), null, 2) : JSON.stringify(v, null, 2) } catch { return v }
}

onMounted(() => {
  fetchOrders()
})
</script>

<style scoped>
/* 工具栏 */
.toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 10px;
  padding: 0 12px;
  min-width: 280px;
  transition: all 0.2s;
}

.search-box:focus-within {
  border-color: #4f46e5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.08);
}

.search-box svg {
  width: 17px;
  height: 17px;
  color: var(--text-3, #9ca3af);
}

.search-box input {
  border: none;
  background: none;
  padding: 10px 0;
  font-size: 14px;
  color: var(--text, #111827);
  outline: none;
  width: 100%;
}

.filter-group {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

.filter-group select {
  padding: 9px 14px;
  border-radius: 8px;
  border: 1px solid var(--border, #e5e7eb);
  background: var(--surface, #fff);
  color: var(--text-2, #374151);
  font-size: 13px;
  outline: none;
  cursor: pointer;
}

.filter-group select:focus {
  border-color: #4f46e5;
}

/* 按钮 */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid transparent;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: #4f46e5;
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  background: #4338ca;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(79, 70, 229, 0.25);
}

.btn-secondary {
  background: var(--bg, #f3f4f6);
  color: var(--text-2, #374151);
  border-color: var(--border, #e5e7eb);
}

.btn-secondary:hover {
  border-color: #4f46e5;
  color: #4f46e5;
}

.btn-danger {
  background: transparent;
  color: #dc2626;
  border: 1px solid #dc2626;
}

.btn-danger:hover:not(:disabled) {
  background: #dc2626;
  color: #fff;
}

.btn-gray {
  background: transparent;
  color: #6b7280;
  border: 1px solid #d1d5db;
}

.btn-gray:hover {
  background: #f3f4f6;
  color: #111827;
  border-color: #9ca3af;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 状态框 */
.state-box {
  text-align: center;
  padding: 60px 0;
  color: var(--text-3, #9ca3af);
  font-size: 14px;
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 14px;
}

.state-box.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.state-box.empty svg {
  width: 48px;
  height: 48px;
  margin: 0 auto 12px;
  color: var(--text-4, #d1d5db);
}

.state-box.empty p {
  font-size: 16px;
  margin: 0 0 4px;
  color: var(--text-2, #6b7280);
}

.state-box.empty span {
  font-size: 13px;
}

.spinner {
  width: 22px;
  height: 22px;
  border: 2.5px solid var(--border, #e5e7eb);
  border-top-color: #4f46e5;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

/* 表格卡片 */
.table-card {
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 14px;
  overflow: hidden;
}

/* 横向滚动容器 */
.table-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.col-order   { width: 220px; min-width: 220px; }
.col-name    { width: 180px; min-width: 120px; }
.col-amount  { width: 90px;  min-width: 90px; }
.col-machine { width: 140px; min-width: 120px; }
.col-status  { width: 80px;  min-width: 80px; }
.col-exec    { width: 80px;  min-width: 80px; }
.col-time    { width: 150px; min-width: 130px; }
.col-action  { width: 60px;  min-width: 60px; }
.col-check   { width: 40px;  min-width: 40px; }

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  table-layout: fixed;
}

.data-table th {
  text-align: left;
  padding: 12px 16px;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--text-3, #9ca3af);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: var(--bg, #f9fafb);
  border-bottom: 1px solid var(--border, #e5e7eb);
  white-space: nowrap;
}

.data-table td {
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-light, #f3f4f6);
  vertical-align: middle;
}

.data-table tbody tr:hover td {
  background: rgba(79, 70, 229, 0.04);
}

.data-table tbody tr:last-child td {
  border-bottom: none;
}

/* 复选框 */
input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #4f46e5;
}

.th-check, .td-check {
  text-align: center;
  padding: 12px 8px !important;
}

tbody tr.selected td {
  background: rgba(79, 70, 229, 0.06) !important;
}

/* 批量删除按钮 */
.btn-danger-batch {
  background: transparent;
  color: #dc2626;
  border: 1px solid #dc2626;
}

.btn-danger-batch:hover:not(:disabled) {
  background: #dc2626;
  color: #fff;
}

.order-id {
  font-family: 'Cascadia Code', 'Fira Code', monospace;
  font-size: 12px;
  background: var(--bg, #f3f4f6);
  padding: 2px 8px;
  border-radius: 4px;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
  white-space: nowrap;
  color: #374151;
}

.name-cell {
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #374151;
}

.mono {
  font-family: 'Cascadia Code', monospace;
  font-size: 12px;
  color: var(--text-2, #6b7280);
}

.amount {
  font-weight: 700;
  color: #059669;
}

.time-col {
  color: var(--text-3, #9ca3af);
  font-size: 12px;
  white-space: nowrap;
}

/* 徽章 */
.badge {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 6px;
  letter-spacing: 0.02em;
}

.badge.ok { background: #ecfdf5; color: #059669; }
.badge.warn { background: #fffbeb; color: #d97706; }
.badge.err { background: #fef2f2; color: #dc2626; }
.badge.muted { background: #f9fafb; color: #6b7280; }
.badge.running { background: #eef2ff; color: #4f46e5; }

/* 链接按钮 */
.btn-link {
  background: none;
  border: none;
  color: #4f46e5;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.15s;
}

.btn-link:hover {
  background: rgba(79, 70, 229, 0.08);
}

/* 分页条 */
.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 20px;
  border-top: 1px solid var(--border-light, #f3f4f6);
  flex-wrap: wrap;
}

.page-size-control {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--text-2, #6b7280);
}

.page-size-control select {
  padding: 5px 8px;
  border: 1px solid var(--border, #e5e7eb);
  border-radius: 6px;
  font-size: 13px;
  color: var(--text, #111827);
  background: var(--surface, #fff);
  cursor: pointer;
  outline: none;
}

.page-size-control select:focus {
  border-color: #4f46e5;
}

.page-info {
  font-size: 13px;
  color: var(--text-2, #6b7280);
  white-space: nowrap;
}

.page-info strong {
  color: var(--text, #111827);
}

.page-nav {
  display: flex;
  align-items: center;
  gap: 4px;
}

.page-nums {
  display: flex;
  align-items: center;
  gap: 3px;
}

.pagination-bar button,
.page-nums button {
  min-width: 34px;
  height: 34px;
  border-radius: 8px;
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e5e7eb);
  color: var(--text-2, #374151);
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.pagination-bar button:hover:not(:disabled),
.page-nums button:hover:not(:disabled) {
  border-color: #4f46e5;
  color: #4f46e5;
}

.pagination-bar button.active,
.page-nums button.active {
  background: #4f46e5;
  border-color: #4f46e5;
  color: #fff;
}

.pagination-bar button:disabled,
.page-nums button:disabled {
  opacity: 0.35;
  cursor: default;
}

.page-nums button.ellipsis {
  border: none;
  background: none;
  cursor: default;
  color: var(--text-3, #9ca3af);
}

/* 弹窗 */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.modal-box {
  background: var(--surface, #fff);
  border-radius: 16px;
  width: 100%;
  max-width: 580px;
  max-height: 88vh;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  border-bottom: 1px solid var(--border, #e5e7eb);
  flex-shrink: 0;
}

.modal-head h3 {
  font-size: 16px;
  font-weight: 700;
  margin: 0;
}

.modal-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.modal-close {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: var(--bg, #f3f4f6);
  color: var(--text-2, #6b7280);
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.modal-close:hover {
  background: #fef2f2;
  color: #dc2626;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  max-height: 60vh;
}

.modal-foot {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid var(--border, #e5e7eb);
  background: var(--bg, #f9fafb);
}

/* 详情网格 */
.detail-grid {
  display: grid;
  grid-template-columns: 120px 1fr;
  column-gap: 20px;
  row-gap: 12px;
}

.dg-row {
  display: contents;
}

.dg-full {
  grid-column: 1 / -1;
}

.dg-k {
  font-size: 13px;
  color: #9ca3af;
  font-weight: 500;
  text-align: right;
  padding-top: 2px;
  align-self: start;
}

.dg-v {
  font-size: 14px;
  color: #374151;
  word-break: break-all;
  align-self: start;
}

.dg-v.strong {
  font-weight: 600;
  color: #111827;
}

.dg-v.amount-lg {
  font-size: 20px;
  font-weight: 800;
  color: #059669;
}

.dg-pre {
  font-size: 12px;
  background: #f3f4f6;
  color: #374151;
  padding: 12px;
  border-radius: 8px;
  margin: 0;
  white-space: pre-wrap;
  font-family: 'Cascadia Code', 'Consolas', monospace;
  max-height: 200px;
  overflow: auto;
}

/* 动画 */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-active .modal-box,
.modal-leave-active .modal-box {
  transition: transform 0.25s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-box,
.modal-leave-to .modal-box {
  transform: scale(0.95) translateY(10px);
}

/* 响应式 */
@media (max-width: 768px) {
  .toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .search-box {
    min-width: unset;
  }

  .filter-group {
    margin-left: 0;
  }

  .data-table {
    font-size: 12px;
  }

  .data-table th,
  .data-table td {
    padding: 8px 12px;
  }

  .dg-row {
    grid-template-columns: 1fr;
    gap: 4px;
  }

  .dg-k {
    text-align: left;
    margin-bottom: 2px;
  }

  .modal-box {
    max-width: 100%;
    margin: 10px;
  }

  .modal-head {
    flex-wrap: wrap;
    gap: 8px;
  }

  .modal-actions {
    flex-wrap: wrap;
  }
}
</style>
